import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { useTeamInfo } from "../../../hooks/useTeams";
import {
  fetchPlayerSeasonStats,
  fetchSeasonStatsMeta,
  type PlayerSeasonStatsParams,
} from "../../../services/services";
import { getErrorMessage } from "../../../services/utils/apiError";
import { IPlayerSeasonStats } from "../../../types/types";
import { PLAYER_ROLES, type PlayerRole } from "../../../utils/leaderboard/roles";
import {
  defaultSeasonStatsSortDirection,
  hasSeasonStats,
  isMultikillSortKey,
  type SeasonStatsRosterFilter,
  type SeasonStatsSortKey,
  type SortDirection,
} from "../../../utils/seasonstats/table";

type SeasonStatsSortState = {
  key: SeasonStatsSortKey;
  direction: SortDirection;
};

const seasonStatsSortReducer = (
  state: SeasonStatsSortState,
  nextKey: SeasonStatsSortKey,
): SeasonStatsSortState => {
  if (isMultikillSortKey(nextKey)) {
    return { key: nextKey, direction: "desc" };
  }

  if (state.key === nextKey) {
    return { ...state, direction: state.direction === "asc" ? "desc" : "asc" };
  }

  return { key: nextKey, direction: defaultSeasonStatsSortDirection(nextKey) };
};

export const useSeasonStats = () => {
  const { strings } = useLocalization();
  const copy = strings.SEASON_STATS;
  const teamInfos = useTeamInfo();

  const [seasons, setSeasons] = useState<string[]>([]);
  const [currentSeason, setCurrentSeason] = useState("");
  const [selectedSeason, setSelectedSeasonState] = useState<string>("");
  const [stats, setStats] = useState<IPlayerSeasonStats[]>([]);
  const [seasonTeamIds, setSeasonTeamIds] = useState<string[]>([]);
  const [teamFilter, setTeamFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [rosterFilter, setRosterFilter] = useState<SeasonStatsRosterFilter>("all");
  const [sort, dispatchSort] = useReducer(seasonStatsSortReducer, {
    key: "kda",
    direction: "desc",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const teamNames = useMemo(
    () => Object.fromEntries((teamInfos ?? []).map((team) => [team.id, team.name])),
    [teamInfos],
  );
  const teamNamesRef = useRef(teamNames);
  teamNamesRef.current = teamNames;

  const setSelectedSeason = (season: string) => {
    setSeasonTeamIds([]);
    setTeamFilter("all");
    setRoleFilter("all");
    setRosterFilter("all");
    setSelectedSeasonState(season);
  };

  useEffect(() => {
    mountedRef.current = true;

    void (async () => {
      try {
        const seasonMeta = await fetchSeasonStatsMeta();
        if (!mountedRef.current) return;

        const selectableSeasons = seasonMeta.seasons;
        setSeasons(selectableSeasons);
        setCurrentSeason(seasonMeta.currentSeason);
        setSelectedSeasonState((current) => {
          if (current && selectableSeasons.includes(current)) return current;
          if (seasonMeta.currentSeason && selectableSeasons.includes(seasonMeta.currentSeason)) {
            return seasonMeta.currentSeason;
          }
          return selectableSeasons[0] || "";
        });
      } catch (err) {
        if (mountedRef.current) setError(getErrorMessage(err, copy.ERROR_SEASONS));
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [copy.ERROR_SEASONS]);

  useEffect(() => {
    if (!selectedSeason) {
      setLoading(false);
      return;
    }

    mountedRef.current = true;
    setLoading(true);
    setError(null);

    const params: PlayerSeasonStatsParams = {
      season: selectedSeason,
      teamId: teamFilter === "all" ? undefined : teamFilter,
      role: roleFilter === "all" ? undefined : (roleFilter as PlayerRole),
      roster: rosterFilter === "all" ? undefined : rosterFilter,
      sortKey: sort.key,
      direction: sort.direction,
    };

    void (async () => {
      try {
        const rows = await fetchPlayerSeasonStats(params);
        if (!mountedRef.current) return;
        setStats(rows);

        if (teamFilter === "all" && roleFilter === "all" && rosterFilter === "all") {
          const names = teamNamesRef.current;
          const ids = [...new Set(rows.filter(hasSeasonStats).map((row) => row.teamId))].sort(
            (a, b) => (names[a] ?? a).localeCompare(names[b] ?? b, "fi"),
          );
          setSeasonTeamIds(ids);
        }
      } catch (err) {
        if (mountedRef.current) setError(getErrorMessage(err, copy.ERROR_PLAYERS));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [
    selectedSeason,
    teamFilter,
    roleFilter,
    rosterFilter,
    sort.key,
    sort.direction,
    copy.ERROR_PLAYERS,
  ]);

  const teamOptions = useMemo(() => {
    if (seasonTeamIds.length) return seasonTeamIds;
    return [...new Set(stats.filter(hasSeasonStats).map((row) => row.teamId))].sort((a, b) =>
      (teamNames[a] ?? a).localeCompare(teamNames[b] ?? b, "fi"),
    );
  }, [seasonTeamIds, stats, teamNames]);

  const formatSeasonLabel = (season: string) =>
    season === currentSeason ? `${season} (${copy.CURRENT_SEASON_LIVE})` : season;

  const isLiveSeason = selectedSeason === currentSeason && currentSeason !== "";

  return {
    copy,
    seasons,
    currentSeason,
    selectedSeason,
    setSelectedSeason,
    visibleRows: stats,
    teamNames,
    teamOptions,
    teamFilter,
    setTeamFilter,
    roleFilter,
    setRoleFilter,
    rosterFilter,
    setRosterFilter,
    sortKey: sort.key,
    sortDirection: sort.direction,
    handleSort: (key: SeasonStatsSortKey) => {
      dispatchSort(key);
    },
    loading,
    error,
    formatSeasonLabel,
    isLiveSeason,
    playerRoles: PLAYER_ROLES,
  };
};
